import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  Users, 
  Play, 
  Pause, 
  BarChart3, 
  UploadCloud, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Activity,
  PhoneForwarded,
  Sparkles,
  Calendar,
  Video
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const Campaigns = () => {
  const [active, setActive] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch campaigns/leads on mount
  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeads(res.data || []);
      } catch (err) {
        // If no leads exist, start with empty array
        setLeads([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaignData();
  }, []);

  // Mock Appointments for Calendar (will be fetched from backend later)
  const [appointments, setAppointments] = useState([]);

  // Simulate live activity
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
        // Randomly update "In Progress" lead duration
        setLeads(prev => prev.map(l => 
            l.status === 'calling' 
            ? { ...l, duration: '1m ' + (parseInt(l.duration.split(' ')[1]) + 1) + 's' } 
            : l
        ));
    }, 1000);
    return () => clearInterval(interval);
  }, [active]);

  const handleStartCampaign = async () => {
    if (active) {
        setActive(false);
        toast.success("Campaign Paused");
        return;
    }

    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/start`, {
            leads: [
                { name: "Acme Corp", phone: "+15550123" },
                { name: "Globex Inc", phone: "+15550124" }
            ],
            agent_id: "agent_default"
        });
        
        setActive(true);
        
        if (res.data.simulation) {
            toast.info("Running in SIMULATION Mode (No Retell Key)");
        } else {
            toast.success(`Neural Dialer Live (Calling from ${res.data.from_number})`);
        }
    } catch (err) {
        toast.error("Failed to start campaign");
    }
  };

  const handleTestCall = async () => {
    const phone = prompt("Enter your phone number (E.164 format, e.g., +12135551234):");
    if (!phone) return;

    try {
        toast.info("Initiating Test Call...");
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/test-call`, {
            to_number: phone,
            agent_id: "agent_4b76ef093069ca0665537e3a53" // Default Retell Agent ID or fetch dynamically
        });
        toast.success("Calling you now! Pick up.");
    } catch (err) {
        toast.error(err.response?.data?.detail || "Test Call Failed");
    }
  };

  return (
    <div className="space-y-8 relative">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
            <h1 className="text-4xl font-bold tracking-tight text-foreground font-heading">Campaigns</h1>
            <Badge variant="outline" className="border-blue-500/30 text-blue-600 bg-blue-50/50">
                COLD CALLING SUITE
            </Badge>
            </div>
            <div className="flex gap-3">
                <Button variant="outline" className="gap-2" onClick={handleTestCall}>
                    <Phone className="h-4 w-4" /> Test Call
                </Button>
                <Button variant="outline" className="gap-2">
                    <UploadCloud className="h-4 w-4" /> Upload CSV
                </Button>
                <Button 
                    className={`${active ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'} text-white gap-2 shadow-lg transition-all`}
                    onClick={handleStartCampaign}
                >
                    {active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    {active ? "Pause Campaign" : "Start Campaign"}
                </Button>
            </div>
        </div>
        <p className="text-muted-foreground text-lg">
          Orchestrate your AI workforce. Upload leads, set scripts, and watch the calls happen in real-time.
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-card border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
                <Users className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-foreground">1,240</div>
                <p className="text-xs text-muted-foreground">+180 from last upload</p>
            </CardContent>
        </Card>
        <Card className="bg-card border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Agents</CardTitle>
                <Activity className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-foreground flex items-center gap-2">
                    {active ? '12' : '0'}
                    {active && <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>}
                </div>
                <p className="text-xs text-muted-foreground">Concurrency limit: 50</p>
            </CardContent>
        </Card>
        <Card className="bg-card border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Connect Rate</CardTitle>
                <PhoneForwarded className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-foreground">34.2%</div>
                <p className="text-xs text-muted-foreground">+2.4% vs avg</p>
            </CardContent>
        </Card>
        <Card className="bg-card border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Meetings Booked</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-foreground">8</div>
                <p className="text-xs text-muted-foreground">Today's performance</p>
            </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="queue" className="w-full">
        <TabsList className="mb-4">
            <TabsTrigger value="queue" className="gap-2"><Phone className="h-4 w-4" /> Live Queue</TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2"><Calendar className="h-4 w-4" /> Appointments</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
            <div className="grid lg:grid-cols-3 gap-8">
                {/* Left: Leads Table */}
                <div className="lg:col-span-2 space-y-6">
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle>Live Queue</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead>Company</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Outcome</TableHead>
                                        <TableHead className="text-right">Duration</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {leads.map((lead, i) => (
                                        <TableRow key={i} className="hover:bg-muted/50">
                                            <TableCell className="font-medium text-foreground">{lead.name}</TableCell>
                                            <TableCell>
                                                {lead.status === 'calling' && (
                                                    <Badge className="bg-blue-500/10 text-blue-600 border-blue-200 animate-pulse">Live Call</Badge>
                                                )}
                                                {lead.status === 'completed' && (
                                                    <Badge variant="outline" className="text-muted-foreground">Completed</Badge>
                                                )}
                                                {lead.status === 'queued' && (
                                                    <Badge variant="outline" className="bg-muted text-muted-foreground">Queued</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {lead.outcome === 'Meeting Booked' ? (
                                                    <span className="text-emerald-600 font-bold flex items-center gap-1">
                                                        <Sparkles className="h-3 w-3" /> Booked
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">{lead.outcome}</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-xs text-muted-foreground">
                                                {lead.duration}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </div>

                {/* Right: Live Neural Feed */}
                <div className="lg:col-span-1 space-y-6">
                    <Card className="bg-slate-950 border-slate-800 text-slate-300 h-[500px] flex flex-col relative overflow-hidden">
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:16px_16px]"></div>
                        <CardHeader className="relative z-10 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
                            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2 text-blue-400">
                                <Activity className="h-4 w-4" /> Neural Feed
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="relative z-10 flex-1 overflow-hidden p-4 font-mono text-xs space-y-4">
                            <div className="flex gap-2 opacity-50">
                                <span className="text-slate-500">[10:42:01]</span>
                                <span>System: Initializing batch #402...</span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-slate-500">[10:42:05]</span>
                                <span className="text-blue-400">Agent-04:</span>
                                <span>"Hello, this is Sarah from FlowFeed."</span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-slate-500">[10:42:08]</span>
                                <span className="text-green-400">Lead:</span>
                                <span>"Hi, what is this about?"</span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-slate-500">[10:42:11]</span>
                                <span className="text-purple-400">Analysis:</span>
                                <span className="italic text-slate-400">Intent detected: Curiosity. Switching to Value_Prop_V2.</span>
                            </div>
                            <div className="flex gap-2">
                                <span className="text-slate-500">[10:42:12]</span>
                                <span className="text-blue-400">Agent-04:</span>
                                <span>"We help contractors like you automate compliance..."</span>
                            </div>
                            {active && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex gap-2"
                                >
                                    <span className="text-slate-500">[10:42:15]</span>
                                    <span className="text-green-400">Lead:</span>
                                    <span>"That sounds interesting..."</span>
                                </motion.div>
                            )}
                            {active && (
                                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-blue-500 animate-pulse">
                                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                                    Live Processing
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </TabsContent>

        <TabsContent value="calendar">
            <Card className="bg-card border-border">
                <CardHeader>
                    <CardTitle>Scheduled Appointments</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                        {appointments.map((appt, i) => (
                            <div key={i} className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors bg-accent/20">
                                <div className="flex items-center justify-between mb-2">
                                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">{appt.date}</Badge>
                                    <span className="text-xs font-mono text-muted-foreground">{appt.time}</span>
                                </div>
                                <h3 className="font-medium text-foreground">{appt.title}</h3>
                                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                    <Video className="h-3 w-3" /> Zoom Meeting
                                </p>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Campaigns;
